import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import { CustomFormBuilder, Loader, useToast } from 'components';
import {
  Criterion,
  Form,
  useCreateForm,
  Recruitment,
  useForm,
  useIsAdmin,
  useRecruitment,
  useUpdateForm,
  useUpdateRecruitment,
  Quota,
  useQuotas,
  NewQuota,
  useUpdateQuotas,
  Field,
} from 'queries';
import { Trans, useTranslation } from 'react-i18next';
import { CheckboxBlockField, CheckboxBlockList } from '@app/form';
import {
  areQuotasValid,
  matchCriteriaWithForm,
} from '/lib/criterionQuotaUtils';
import { v4 as uuidv4 } from 'uuid';

interface ScreeningBuilderFormChildrenOptions<T = any> {
  builderNode: React.ReactNode;
  submit: (e: SyntheticEvent, extraParams?: T) => any;
  profileValidationNode: React.ReactNode;
  addFieldWithCriterion: (field: Field, criterion: Criterion) => void;
  addFieldsWithCriteria: (
    items: { field: Field; criterion?: Criterion }[],
  ) => void;
  currentForm: Partial<Form> | undefined;
}

interface Props<T = any> {
  recruitmentId?: string;
  sessionId?: string;
  children: (
    options: ScreeningBuilderFormChildrenOptions<T>,
  ) => React.ReactNode;
  onSubmit?: (extraParams?: T) => any;
  onSuccess?: (recruitment: Recruitment, form?: Form, extraParams?: T) => any;
  onError?: (e: Error, extraParams?: T) => any;
  onFinish?: (extraParams?: T) => any;
  organizationId?: string;
  maxNumberOfQuestions?: number;
  numberOfParticipantRequired?: number;
}

const useStyles = createUseStyles((theme: Theme) => ({
  checkboxDetails: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase,
    color: theme.colors.inkSecondary,
  },
}));

export const ScreeningBuilderForm = function <T extends any = any>({
  recruitmentId,
  sessionId,
  children,
  onSuccess,
  onSubmit: onSubmitProps,
  onError,
  onFinish,
  organizationId,
  maxNumberOfQuestions,
  numberOfParticipantRequired,
}: Props<T>) {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const toast = useToast();
  const { t } = useTranslation();

  const isAdmin = useIsAdmin();
  const { data: recruitment, isLoading: recruitmentIsLoading } =
    useRecruitment(recruitmentId);

  const formId = recruitment ? recruitment.screeningFormId : undefined;
  const { data: form, isLoading: formIsLoading } = useForm(formId);
  const isLoading = !form && (recruitmentIsLoading || formIsLoading);

  const [newForm, setNewForm] = useState<Partial<Form> | undefined>(undefined);

  const [criteria, onCriteriaChange] = useState<Criterion[] | undefined>(
    undefined,
  );
  const [quotas, onQuotaChange] = useState<Quota[] | undefined>(undefined);

  const { mutateAsync: updateRecruitment } = useUpdateRecruitment(
    recruitment && recruitment.id,
  );
  const { mutateAsync: createForm } = useCreateForm(organizationId);
  const { mutateAsync: updateForm } = useUpdateForm(formId);
  const { mutateAsync: updateQuotas } = useUpdateQuotas(sessionId);

  const [definedAutoValidation, setAutomaticValidation] = useState<
    boolean | undefined
  >(undefined);
  const automaticValidation =
    definedAutoValidation !== undefined
      ? definedAutoValidation
      : recruitment?.automaticValidation || false;

  const { data: loadedQuotas } = useQuotas(sessionId, null, false);

  const newQuotaToQuota = (nq: NewQuota): Quota => {
    return {
      ...nq,
      value: nq.max!,
    };
  };

  useEffect(() => {
    if (loadedQuotas) {
      onQuotaChange(loadedQuotas.map(newQuotaToQuota));
    }
  }, [loadedQuotas]);

  const verifiedCriteria = useMemo(() => {
    return matchCriteriaWithForm(form, recruitment?.criteria);
  }, [form, recruitment?.criteria]);

  const allCriteria = useMemo(() => {
    if (criteria) {
      return criteria;
    }
    if (recruitment?.criteria) {
      return verifiedCriteria;
    }
    return [];
  }, [criteria, recruitment, verifiedCriteria]);

  const demographicCriteria = useMemo(
    () => allCriteria.filter((c) => !c.screenerCriterion),
    [allCriteria],
  );
  const screenerCriteria = useMemo(
    () => allCriteria.filter((c) => !!c.screenerCriterion),
    [allCriteria],
  );

  const onValidationModeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value === 'true';
      setAutomaticValidation(value);
    },
    [setAutomaticValidation],
  );

  const addFieldsWithCriteria = useCallback(
    (
      items: { field: Field; criterion?: Criterion }[],
      position: 'start' | 'end' = 'end',
    ) => {
      const currentFields = (newForm || form)?.fields || [];
      const addedFields = items.map(({ field }) => field);
      const updatedForm = {
        ...(newForm || form || { id: uuidv4(), fields: [] }),
        fields:
          position === 'start'
            ? [...addedFields, ...currentFields]
            : [...currentFields, ...addedFields],
      };
      setNewForm(updatedForm);

      const currentCriteria =
        criteria || (recruitment && recruitment.criteria) || [];
      onCriteriaChange([
        ...currentCriteria,
        ...items.flatMap(({ criterion }) => (criterion ? [criterion] : [])),
      ]);
    },
    [newForm, form, criteria, recruitment],
  );

  const addFieldWithCriterion = useCallback(
    (field: Field, criterion: Criterion) => {
      addFieldsWithCriteria([{ field, criterion }], 'start');
      toast.success(t('editSession.screening.gdpr.added'));
    },
    [addFieldsWithCriteria, toast, t],
  );

  const onSubmit = async (e: SyntheticEvent, extraParams?: T) => {
    e.preventDefault();

    try {
      if (!areQuotasValid(screenerCriteria)) {
        toast.error(t('editSession.targeting.quotas.rangeNotCovered'));
        return;
      }

      if (onSubmitProps) {
        onSubmitProps(extraParams);
      }
      let _form: Form | undefined = form;

      if (newForm) {
        if (!newForm.fields || !newForm.fields.length) {
          _form = undefined;
        } else if (_form) {
          _form = await updateForm(newForm);
        } else {
          _form = await createForm({ ...newForm, organizationId });
        }
      }
      const verifiedScreenerCriteria = matchCriteriaWithForm(
        _form,
        screenerCriteria,
      );
      const res = await updateRecruitment({
        id: recruitmentId,
        screeningFormId: (_form && _form.id) || null,
        criteria: [...demographicCriteria, ...verifiedScreenerCriteria],
        automaticValidation: automaticValidation,
      });

      await updateQuotas(quotas || []);

      toast.saved();
      if (onSuccess) {
        await onSuccess(res, _form, extraParams);
      }
    } catch (e) {
      toast.exception(e);
      if (onError && e instanceof Error) {
        onError(e, extraParams);
      }
    } finally {
      if (onFinish) {
        onFinish(extraParams);
      }
    }
  };

  return (
    <form onSubmit={onSubmit}>
      {children({
        submit: onSubmit,
        profileValidationNode: (
          <CheckboxBlockList>
            <div>
              <CheckboxBlockField
                label={t(
                  'editSession.screening.profileValidation.manuallyChoice',
                )}
                value="false"
                checked={!automaticValidation}
                type="radio"
                variant="green"
                onChange={onValidationModeChange}
              />
              <div className={classes.checkboxDetails}>
                <Trans i18nKey="editSession.screening.profileValidation.manuallyDetails" />
              </div>
            </div>
            <div>
              <CheckboxBlockField
                label={t(
                  'editSession.screening.profileValidation.automaticChoice',
                )}
                value="true"
                checked={automaticValidation}
                type="radio"
                variant="green"
                onChange={onValidationModeChange}
              />
              <div className={classes.checkboxDetails}>
                <Trans i18nKey="editSession.screening.profileValidation.automaticDetails" />
              </div>
            </div>
          </CheckboxBlockList>
        ),
        builderNode: isLoading ? (
          <Loader />
        ) : (
          <CustomFormBuilder
            form={newForm || form}
            onChange={setNewForm}
            criteria={criteria || (recruitment && recruitment.criteria) || []}
            onCriteriaChange={onCriteriaChange}
            maxNumberOfQuestions={isAdmin ? -1 : maxNumberOfQuestions}
            sessionId={sessionId}
            numberOfParticipantRequired={numberOfParticipantRequired}
            quotas={quotas}
            onQuotaChange={onQuotaChange}
          />
        ),
        addFieldWithCriterion,
        addFieldsWithCriteria,
        currentForm: newForm || form,
      })}
    </form>
  );
};
